import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';
import { applicable, every, itemKind, loincCode, patient, taskCategory } from './builders';

export const HYPERLIPIDEMIA_MONITORING_URL = 'https://liza.health/fhir/PlanDefinition/hyperlipidemia-monitoring';
export const HYPERLIPIDEMIA_MONITORING_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const HYPERLIPIDEMIA_MONITORING_CANONICAL = `${HYPERLIPIDEMIA_MONITORING_URL}|${HYPERLIPIDEMIA_MONITORING_VERSION}`;

/**
 * The 2018 cholesterol guideline is retired and said every 3-12 months. This is the 2026 guideline
 * that replaced it, which says 6-12; citing the wrong edition would attach a real number to the wrong
 * document, which no test catches.
 */
const ACC_AHA_LIPIDS_2026 = {
  title: 'ACC/AHA/Multisociety Guideline on the Management of Blood Cholesterol (2026)',
  url: 'https://www.ahajournals.org/cholesterol-guidelines',
} as const;

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const HYPERLIPIDEMIA_CODES = {
  LDL: LOINC_CODES.LDL_CHOLESTEROL,
  HDL: LOINC_CODES.HDL_CHOLESTEROL,
  TRIGLYCERIDES: LOINC_CODES.TRIGLYCERIDES,
  TOTAL_CHOLESTEROL: LOINC_CODES.TOTAL_CHOLESTEROL,
} as const;

/** The variables this protocol binds before evaluating any condition. */
export const HYPERLIPIDEMIA_CONTEXT_VARIABLES = [
  {
    name: '%onLipidLoweringTherapy',
    description: 'Boolean: an active MedicationRequest whose ingredient RxCUI is in the statin ValueSet.',
  },
] as const;

export type HyperlipidemiaContextVariable = (typeof HYPERLIPIDEMIA_CONTEXT_VARIABLES)[number]['name'];

/**
 * `E78` is the ICD-10 category for disorders of lipoprotein metabolism, covering `E78.0` through
 * `E78.5`, matched as a prefix. The SNOMED code is matched exactly.
 */
export const HYPERLIPIDEMIA_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E78', display: 'Disorders of lipoprotein metabolism' },
  { system: CODE_SYSTEMS.SNOMED, code: '55822004', display: 'Hyperlipidemia' },
] as const;

const citation = (locus: string): PlanDefinitionAction['documentation'] => [
  { type: 'citation', display: `${ACC_AHA_LIPIDS_2026.title}, ${locus}`, url: ACC_AHA_LIPIDS_2026.url },
];

/**
 * A lipid panel component. The four are drawn together and share one cadence, because a lipid panel
 * is one draw rather than four independent results — the same reasoning the MASLD protocol uses for
 * the labs FIB-4 is calculated from.
 *
 * Six months is the shorter end of the guideline's 6-12 month range, resolved the way every other
 * range in these protocols is: to the end that satisfies the guideline whichever way it is read.
 */
const lipidPanelComponent = (id: string, title: string, code: string): PlanDefinitionAction => ({
  id,
  title,
  description: 'Lipid panel every 6 months while on lipid-lowering therapy.',
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  condition: applicable('%onLipidLoweringTherapy', 'On lipid-lowering therapy'),
  timingTiming: every(6, 'mo'),
  documentation: citation('Section 3.5 Recommendation 1'),
});

// --- actions ------------------------------------------------------------------------------------------

const ldl = lipidPanelComponent('ldl', 'LDL cholesterol', HYPERLIPIDEMIA_CODES.LDL);
const hdl = lipidPanelComponent('hdl', 'HDL cholesterol', HYPERLIPIDEMIA_CODES.HDL);
const triglycerides = lipidPanelComponent('triglycerides', 'Triglycerides', HYPERLIPIDEMIA_CODES.TRIGLYCERIDES);
const totalCholesterol = lipidPanelComponent(
  'total-cholesterol',
  'Total cholesterol',
  HYPERLIPIDEMIA_CODES.TOTAL_CHOLESTEROL,
);

/**
 * No cadence, and not conditional on therapy: a patient with the diagnosis who is not on a statin
 * still has lipids worth tracking, but the guideline gives an interval only for those on treatment.
 * As-needed puts the panel on the plan for everyone else without asserting one.
 */
const ldlUntreated: PlanDefinitionAction = {
  id: 'ldl-untreated',
  title: 'LDL cholesterol',
  description: 'LDL cholesterol, as needed, while not on lipid-lowering therapy.',
  code: loincCode(HYPERLIPIDEMIA_CODES.LDL),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  condition: applicable('%onLipidLoweringTherapy.not()', 'Not on lipid-lowering therapy'),
  documentation: citation('Section 3.5'),
};

// --- the protocol -------------------------------------------------------------------------------------

/**
 * What the 2026 cholesterol guideline asks for on an ongoing basis in hyperlipidemia.
 *
 * **Liver enzymes and CK are deliberately absent, and this is the one absence here that is not a
 * gap.** The issue body lists ALT/AST "if on statins" and CK "if muscle symptoms" as ongoing items.
 * The guideline recommends *against* both as routine measurements — Section 5.1 Recommendation 6 puts
 * routine aminotransferases at COR 3 (No Benefit), and Recommendation 5 does the same for CK at LOE A.
 * CK has a real role, but only as a one-time symptom-triggered test in a patient with severe myalgias
 * or weakness, which is not a cadence and not something a protocol schedules.
 *
 * Putting either on the plan as-needed would be wrong in a way the other as-needed items are not:
 * "tracked, no schedule" reads as "worth watching", and the guideline's position is that watching
 * these in an asymptomatic patient has no benefit.
 *
 * **A three-way conflict a reviewer should know about.** For a patient on a statin, three current
 * guidelines give three intervals: this one says every 6-12 months, the 2023 chronic coronary disease
 * guideline says 3-12, and ADA Standards Recommendation 10.17 says annually. All three agree on the
 * 4-12 week check after starting or changing a dose. A patient with coronary disease and diabetes on
 * a statin matches all three. This protocol uses the 2026 dyslipidemia number because it is the most
 * current statement on lipids specifically; the CAD/HF protocol will have to decide whether to repeat
 * that or defer, and the engine's more-frequent-wins dedup decides what a clinician actually sees.
 */
export const hyperlipidemiaMonitoring: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: HYPERLIPIDEMIA_MONITORING_URL,
  version: HYPERLIPIDEMIA_MONITORING_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: HYPERLIPIDEMIA_MONITORING_URL }],
  name: 'hyperlipidemia-monitoring',
  title: 'Hyperlipidemia — ongoing monitoring',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-16',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in hyperlipidemia, expanded onto a care-plan topic when a hyperlipidemia Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...HYPERLIPIDEMIA_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: ACC_AHA_LIPIDS_2026.title, url: ACC_AHA_LIPIDS_2026.url }],
  action: [
    {
      id: 'ongoing-monitoring',
      title: 'Ongoing monitoring',
      description: 'Results tracked on an ongoing basis for hyperlipidemia.',
      action: [ldl, hdl, triglycerides, totalCholesterol, ldlUntreated],
    },
  ],
};
